import { ArrowRight } from "lucide-react";
import { useContent } from "@/lib/ContentContext";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { ClosingCTA } from "@/pages/Home";

export default function Company() {
  const { timeline, values } = useContent();
  return (
    <>
      <section className="border-b border-[#D8E2E6]" data-testid="company-hero">
        <div className="container-riq pt-16 pb-20 lg:pt-24 lg:pb-24">
          <p className="eyebrow mb-5">Company</p>
          <h1 className="font-display font-black tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl max-w-4xl">
            <MaskedLine delay={0.1}>We ran the sell side.</MaskedLine>
            <MaskedLine delay={0.22}>
              Now we fight for the <span className="accent-word">buy side.</span>
            </MaskedLine>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-[#4A5A63] leading-relaxed max-w-2xl">
            RresponsIQ is built by Xyle, the company behind MonetiseUP. Running publisher-side auction infrastructure showed us exactly where advertisers lose money to opacity — so we built the DSP that gives it back.
          </p>
        </div>
      </section>

      <section className="py-20 lg:py-28 panel-dark border-b border-[#D8E2E6]" data-testid="both-sides-story">
        <div className="container-riq grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <p className="eyebrow mb-3">The both-sides story</p>
            <h2 className="font-display font-black tracking-tight text-2xl sm:text-3xl lg:text-4xl text-[#F7F9FA]">
              We've seen the auction from <span className="accent-word">every seat.</span>
            </h2>
            <p className="mt-5 text-[#F7F9FA]/70 leading-relaxed">
              MonetiseUP's sell-side stack processes billions of auction requests. From that vantage point, one pattern was impossible to ignore: advertisers paying bundled CPMs with no idea what the media actually cost, while layers of intermediaries took undisclosed cuts.
            </p>
            <p className="mt-4 text-[#F7F9FA]/70 leading-relaxed">
              RresponsIQ is the answer we wished existed: a buy-side platform engineered on exchange-grade infrastructure, priced with radical simplicity, and accountable to the people signing the checks.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="grid grid-cols-2 gap-4" data-testid="both-sides-diagram">
              <div className="border-2 border-[#F7F9FA]/30 p-6 text-center">
                <p className="font-mono2 text-[10px] uppercase tracking-widest text-[#F7F9FA]/50">May 2024</p>
                <p className="font-display font-black text-2xl text-[#F7F9FA] mt-2">MonetiseUP</p>
                <p className="text-xs text-[#F7F9FA]/60 mt-2">Sell-side infrastructure. Publisher yield, maximized.</p>
              </div>
              <div className="border border-[#32A2C5] bg-[#32A2C5]/10 rounded-lg p-6 text-center">
                <p className="font-mono2 text-[10px] uppercase tracking-widest text-[#32A2C5]">Sep 2024 →</p>
                <p className="font-display font-black text-2xl text-[#F7F9FA] mt-2">RresponsIQ</p>
                <p className="text-xs text-[#F7F9FA]/60 mt-2">Buy-side platform. Advertiser transparency, guaranteed.</p>
              </div>
              <div className="col-span-2 border-2 border-[#F7F9FA]/30 p-5 text-center">
                <p className="font-mono2 text-xs uppercase tracking-widest text-[#F7F9FA]/70">
                  One auction. Both sides understood. <span className="text-[#32A2C5]">Zero secrets.</span>
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-28" data-testid="values-section">
        <div className="container-riq">
          <Reveal>
            <p className="eyebrow mb-3">Values</p>
            <h2 className="font-display font-black tracking-tight text-2xl sm:text-3xl lg:text-4xl">
              What we <span className="accent-word">optimize for.</span>
            </h2>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {values.map((v, i) => (
              <Reveal key={v.id} delay={i * 0.1}>
                <div className="card-brut-sm card-hover p-6 h-full" data-testid={`value-${v.id}`}>
                  <span className="font-display font-black text-4xl text-[#0E7A8F]">0{i + 1}</span>
                  <h3 className="font-display font-bold text-xl tracking-tight mt-3">{v.name}</h3>
                  <p className="text-sm text-[#4A5A63] mt-2 leading-relaxed">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-[#EDF3F5] border-y border-[#D8E2E6]" data-testid="timeline-section">
        <div className="container-riq">
          <Reveal>
            <p className="eyebrow mb-3">Timeline</p>
            <h2 className="font-display font-black tracking-tight text-2xl sm:text-3xl lg:text-4xl mb-12">
              From infrastructure to <span className="accent-word">insurrection.</span>
            </h2>
          </Reveal>
          <div className="relative border-l-2 border-[#D8E2E6] ml-3 space-y-10">
            {timeline.map((t, i) => (
              <Reveal key={t.year} delay={i * 0.06}>
                <div className="relative pl-10" data-testid={`timeline-${t.year}`}>
                  <span className="absolute -left-[11px] top-1 w-5 h-5 bg-[#0E7A8F] border border-[#D8E2E6]" aria-hidden="true" />
                  <p className="font-mono2 text-xs font-bold tracking-widest text-[#0E7A8F]">{t.year}</p>
                  <h3 className="font-display font-bold text-xl tracking-tight mt-1">{t.title}</h3>
                  <p className="text-sm text-[#4A5A63] mt-1.5 max-w-xl leading-relaxed">{t.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-14">
            <a href="/book-demo" className="btn-primary" data-testid="company-cta-btn">
              Join the next chapter <ArrowRight className="w-4 h-4" />
            </a>
          </Reveal>
        </div>
      </section>
      <ClosingCTA />
    </>
  );
}
